import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { web } from "../../lib/api";

/** Hero search for fans: finds teams whose coach made the team page public. */
export const TeamSearch = () => {
  const [q, setQ] = useState("");
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const box = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setTeams([]); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      web.get("/public/teams", { params: { q: term } })
        .then(({ data }) => setTeams(Array.isArray(data?.teams) ? data.teams : []))
        .catch(() => setTeams([]))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const go = (t) => navigate(`/team/${t.slug}`);
  const showList = open && q.trim().length >= 2;
  return (
    <div ref={box} className="relative w-full max-w-xl" data-testid="team-search">
      <form onSubmit={(e) => { e.preventDefault(); if (teams[0]) go(teams[0]); }}
        className="flex items-center gap-3 rounded-full border border-white/15 bg-[#0F172A]/80 px-5 py-3.5 backdrop-blur-md transition-colors focus-within:border-[#39FF14]/70">
        <Search className="h-5 w-5 shrink-0 text-[#39FF14]" />
        <input data-testid="team-search-input" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
          placeholder="Find a team: name, school or city" aria-label="Search public team pages"
          className="w-full bg-transparent text-base text-white placeholder:text-slate-400 focus:outline-none" />
        {busy && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />}
      </form>
      {showList && (
        <ul data-testid="team-search-results" className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-[#1E293B] bg-[#0A0E1A]/95 shadow-2xl backdrop-blur-xl">
          {teams.length ? teams.map((t) => (
            <li key={t.slug}>
              <button type="button" data-testid={`team-search-result-${t.slug}`} onClick={() => go(t)}
                className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors hover:bg-[#162032]">
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-white">{t.team_name}</span>
                  {t.city && <span className="block truncate text-sm text-slate-400">{t.city}</span>}
                </span>
                {t.record && <span className="shrink-0 font-mono-gd text-sm text-[#39FF14]">{`${t.record.w}-${t.record.l}`}</span>}
              </button>
            </li>
          )) : !busy && <li data-testid="team-search-empty" className="px-5 py-4 text-sm text-slate-400">No public team pages match. Coaches can turn theirs on under Team access.</li>}
        </ul>
      )}
    </div>
  );
};
